import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'chatContainer',
    'availabilityContainer',
    'input',
    'history',
    'availabilityHistory',
    'createServiceEventButton',
    'serviceProviderSelect',
    'bookableResourceSelect',
    'pickADate',
    'startTime',
    'successMessage',
    'chooseDateField',
    'primaryActionButton',
    'availabilitySection',
    'tools',
    'chatConversation',
  ];

  connect() {
    console.log('Student Services Chat controller connected');
    this.threadId = null;
    this.serviceDetails = null;
    //this.pickADateTarget.addEventListener("input", this.chooseDate.bind(this));
  }

  setThreadId(id) {
    this.threadId = id; // Set thread ID after creation
  }

  setServiceDetails(details) {
    this.serviceDetails = details; // Store service details
  }

  toggleChat(event) {
    const serviceId = event.target.value;
    if (serviceId) {
      this.availabilityContainerTarget.classList.remove('hidden'); // Show
      this.chatContainerTarget.classList.remove('hidden'); // Show
      // The times panel stays hidden until we actually have times to show, so
      // it doesn't take up space as an empty box. Reset it on service change.
      this.setAvailabilityVisible(false);
      this.resetDateSelection();
      // Start in "tools" mode: show the check-availability/date controls and
      // hide the chat until the student runs an initial availability check.
      this.resetToToolsMode();
      this.createThread(serviceId);
    } else {
      this.availabilityContainerTarget.classList.add('hidden'); // Hide
      this.chatContainerTarget.classList.add('hidden'); // Hide
      this.setAvailabilityVisible(false);
    }
  }

  createThread(serviceId) {
    fetch('/student/services/create_thread', {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ service_id: serviceId }),
    })
      .then(response => response.json())
      .then(data => {
        if (data.thread_id) {
          console.log('Thread created with ID:', data.thread_id);
          this.setThreadId(data.thread_id);
          this.fetchServiceDetails(serviceId); // Fetch service details
        } else {
          console.error('Failed to create thread:', data.error);
        }
      })
      .catch(error => console.error('Error creating thread:', error));
  }

  fetchServiceDetails(serviceId) {
    // Construct the URL dynamically based on the current location
    const url = `/student/service_details/${serviceId}`;

    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        if (data.error) {
          console.error('Error fetching service details:', data.error);
          return;
        }

        // Set service details for instructions
        this.setServiceDetails(data.message);

        // Populate the instructions textarea
        const instructionsTextarea = document.getElementById(
          'instructionsTextarea'
        );
        instructionsTextarea.value = this.prependCurrentDate(data.message);

        // Populate service provider dropdown
        const serviceProviderSelect = document.getElementById(
          'service_provider_select'
        );
        serviceProviderSelect.innerHTML = ''; // Clear existing options
        data.serviceProviders.forEach(provider => {
          const option = document.createElement('option');
          option.value = provider.id;
          option.textContent = provider.name;
          serviceProviderSelect.appendChild(option);
        });

        // Populate bookable resource dropdown
        const bookableResourceSelect = document.getElementById(
          'bookable_resource_select'
        );
        bookableResourceSelect.innerHTML = ''; // Clear existing options
        data.bookableResources.forEach(resource => {
          const option = document.createElement('option');
          option.value = resource.id;
          option.textContent = resource.name;
          bookableResourceSelect.appendChild(option);
        });

        // Update hidden fields for required service providers and resources
        const numServiceProvidersField = document.getElementById(
          'num_service_providers_required'
        );
        const numBookableResourcesField = document.getElementById(
          'num_bookable_resources_required'
        );
        if (numServiceProvidersField && numBookableResourcesField) {
          numServiceProvidersField.value =
            data.numServiceProvidersRequired || 0;
          numBookableResourcesField.value =
            data.numBookableResourcesRequired || 0;
          console.log('Hidden fields updated with service requirements:', {
            numServiceProvidersRequired: data.numServiceProvidersRequired,
            numBookableResourcesRequired: data.numBookableResourcesRequired,
          });
        } else {
          console.error('Hidden fields for service requirements not found.');
        }

        console.log(
          'Dropdowns populated with service providers and bookable resources.'
        );
      })
      .catch(error => console.error('Error fetching service details:', error));
  }

  prependCurrentDate(message) {
    const now = new Date();
    const currentDate = now.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const currentTime = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `**Today is ${currentDate} and the time is ${currentTime}.**\n\n${message}`;
  }

  send(event) {
    event.preventDefault(); // Prevent the form from submitting
    const message = this.inputTarget.value;
    this.inputTarget.value = ''; // Clear input after sending
    const userMessageElement = document.createElement('div');
    userMessageElement.classList.add('chatgpt-message', 'user');
    userMessageElement.textContent = `You: ${message}`;
    this.historyTarget.appendChild(userMessageElement);

    if (message.trim() !== '' && this.threadId) {
      this.scrollToBottom();

      // Show loading indicator
      this.showLoadingIndicator();
      const instructionsTextarea = document.getElementById(
        'instructionsTextarea'
      );
      const editedInstructions = this.prependCurrentDate(
        instructionsTextarea ? instructionsTextarea.value : this.serviceDetails
      );

      this.createRunWithInstructions(message, editedInstructions);
    } else {
      console.error('Message is empty or thread ID is not set.');
      this.displayAssistantMessage(
        'Failed to send message. Thread ID is not set.'
      );
      this.addToAvailabilityHistory(
        'Failed to send message. Thread ID is not set.'
      );
    }
  }

  createRunWithInstructions(message, instructions = '') {
    const additionalMessages = [
      {
        role: 'user',
        content: instructions || this.serviceDetails,
      },
    ];

    fetch('/student/services/availability_chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
      },
      body: JSON.stringify({
        message: message,
        thread_id: this.threadId,
        additional_messages: additionalMessages,
      }),
    })
      .then(response => response.json())
      .then(data => {
        if (data.response) {
          this.processAssistantResponse(data.response);
          this.removeLoadingIndicator();
        } else if (data.error) {
          console.error('Error from AI:', data.error);
          this.displayAssistantMessage(
            'Error processing your request. Please try again.'
          );
          this.addToAvailabilityHistory(
            'Error processing your request. Please try again.'
          );
        }
      })
      .catch(error => {
        console.error('Error:', error);
        this.displayAssistantMessage(
          'Failed to connect. Please check your connection and try again.'
        );
        this.addToAvailabilityHistory(
          'Failed to connect. Please check your connection and try again.'
        );
      });
  }

  displayAssistantMessage(html) {
    const assistantMessageElement = document.createElement('div');
    assistantMessageElement.classList.add('chatgpt-message', 'assistant');

    const span = document.createElement('span');
    span.className = 'assistant-message__text';
    assistantMessageElement.append(
      document.createTextNode('Availability Assistant: '),
      span
    );
    this.historyTarget.appendChild(assistantMessageElement);

    this.typeAssistantText(span, html);

    // No longer parsing the message for date/time here.
    // Confirmation button logic is centralized elsewhere.
    console.log('Assistant message displayed:', html);
  }

  // Reveal the reply smoothly: type its plain text (no per-character innerHTML
  // re-parse, scrolling at most once per frame) to avoid the layout thrash that
  // made long replies feel like the page was loading on mobile, then swap in the
  // rendered HTML so formatting (<p>, <strong>) shows once typing finishes.
  typeAssistantText(target, html) {
    const safeHtml = html || '';
    const plainText = this.htmlToPlainText(safeHtml);

    const finalize = () => {
      target.classList.remove('assistant-message__text--typing');
      target.innerHTML = safeHtml;
      this.scrollToBottom();
    };

    const reduceMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion || !plainText) {
      finalize();
      return;
    }

    target.classList.add('assistant-message__text--typing');
    const charsPerSecond = 280;
    let shown = 0;
    let previous = null;

    const step = timestamp => {
      if (previous === null) previous = timestamp;
      shown = Math.min(
        plainText.length,
        shown + (charsPerSecond * (timestamp - previous)) / 1000
      );
      previous = timestamp;
      target.textContent = plainText.slice(0, Math.floor(shown));
      this.scrollToBottom();
      if (shown < plainText.length) {
        requestAnimationFrame(step);
      } else {
        finalize();
      }
    };

    requestAnimationFrame(step);
  }

  // Plain-text version of the reply for the typing animation, with block-level
  // tags turned into line breaks so paragraphs still read naturally.
  htmlToPlainText(html) {
    const withBreaks = String(html || '')
      .replace(/<\/(p|div)>/gi, '$&\n\n')
      .replace(/<br\s*\/?>/gi, '\n');
    const doc = new DOMParser().parseFromString(withBreaks, 'text/html');
    return (doc.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
  }

  addToAvailabilityHistory(htmlText) {
    this.availabilityHistoryTarget.innerHTML = ''; // Clear existing content
    const availabilityMessageElement = document.createElement('div');
    availabilityMessageElement.innerHTML = htmlText;
    this.availabilityHistoryTarget.appendChild(availabilityMessageElement);
  }

  processAssistantResponse(lastMessage) {
    const confirmationRegex = /<div>([\s\S]*?)<\/div>/;
    const confirmationMatch = lastMessage.match(confirmationRegex);
    const confirmationMessage = confirmationMatch
      ? confirmationMatch[0].trim()
      : '';

    const htmlSnippetRegex = /<table[\s\S]*?<\/table>/;
    const htmlMatch = lastMessage.match(htmlSnippetRegex);
    const htmlSnippet = htmlMatch ? htmlMatch[0].trim() : '';

    this.updateAvailabilityHistory(confirmationMessage, htmlSnippet);
    const hasBookingTimes = this.renderBookingOptions(
      htmlSnippet,
      confirmationMessage
    );
    // Only reveal the times panel once we actually have appointment times.
    this.setAvailabilityVisible(hasBookingTimes);

    const cleanedMessage = lastMessage
      .replace(confirmationRegex, '')
      .replace(htmlSnippetRegex, '')
      .trim();
    if (cleanedMessage) {
      this.displayAssistantMessage(cleanedMessage);
    }
  }

  fetchThreadMessages() {
    fetch(
      `/student/services/fetch_thread_messages?thread_id=${this.threadId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
      }
    )
      .then(response => response.json())
      .then(data => {
        if (data.messages && data.messages.length > 0) {
          this.processAssistantResponse(data.messages[0]);
        } else {
          console.error('Failed to fetch messages:', data.error);
          this.displayAssistantMessage(
            'Failed to retrieve conversation history.'
          );
        }
        this.removeLoadingIndicator();
      })
      .catch(error => {
        console.error('Error fetching messages:', error);
        this.displayAssistantMessage('Error retrieving messages.');
        this.removeLoadingIndicator();
      });
  }

  updateAvailabilityHistory(confirmationMessage, htmlSnippet) {
    console.log('updateAvailabilityHistory called.');
    console.log('Raw Confirmation Message:', confirmationMessage);
    console.log('Raw HTML Snippet:', htmlSnippet);

    // Clear existing content
    this.availabilityHistoryTarget.innerHTML = '';

    // Create a wrapper container for header and confirmation
    const wrapperContainer = document.createElement('div');
    wrapperContainer.className = 'availability-wrapper';

    // Create header for appointment request
    const headerElement = document.createElement('h4');
    headerElement.className = 'suggestion-header';
    headerElement.textContent = 'Appointment Request';

    // Create confirmation message container
    const confirmationContainer = document.createElement('div');
    confirmationContainer.className = 'confirmation-container';

    // Parse the confirmation message HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(confirmationMessage, 'text/html');

    // Traverse <p> elements, filter out "none", and append meaningful ones
    const paragraphs = doc.querySelectorAll('p');
    paragraphs.forEach(p => {
      const strongText = p.querySelector('strong')?.textContent.trim();
      const content = p.textContent.replace(strongText, '').trim();

      if (content.toLowerCase() !== 'none') {
        confirmationContainer.appendChild(p.cloneNode(true));
      } else {
        console.log(
          `Skipping field with strongText '${strongText}' as it is 'none'.`
        );
      }
    });

    // Append confirmationContainer to the wrapper
    wrapperContainer.appendChild(headerElement);
    wrapperContainer.appendChild(confirmationContainer);

    this.availabilityHistoryTarget.appendChild(wrapperContainer);

    if (
      this.parseBookingOptions(htmlSnippet, confirmationMessage).length === 0
    ) {
      const htmlContainer = document.createElement('div');
      htmlContainer.className = 'html-container';
      htmlContainer.innerHTML = htmlSnippet || '';
      this.availabilityHistoryTarget.appendChild(htmlContainer);
    }

    // Extract Date, Time, Service Providers, and Resources
    let dateText = null;
    let timeText = null;

    paragraphs.forEach(p => {
      const strongText = p.querySelector('strong')?.textContent.trim();
      const content = p.textContent.replace(strongText, '').trim();

      if (content.toLowerCase() === 'none') {
        return; // Skip 'none' entries
      }

      switch (strongText) {
        case 'Date:':
          dateText = content;
          break;
        case 'Time:':
          timeText = content;
          break;
      }
    });

    console.log('Extracted Date:', dateText);
    console.log('Extracted Time:', timeText);

    if (dateText && timeText) {
      const [startTime, endTime] = timeText
        .split('to')
        .map(time => time.trim());

      console.log('Parsed Start Time:', startTime);
      console.log('Parsed End Time:', endTime);

      // Update the button
      this.updateConfirmationButton(dateText, startTime, endTime);
    } else {
      console.warn('Date or Time could not be extracted.');
      this.hideConfirmationButton();
    }
  }

  parseConfirmationHtml(confirmationMessage) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(confirmationMessage || '', 'text/html');
    const details = {};

    doc.querySelectorAll('p').forEach(p => {
      const label = p.querySelector('strong')?.textContent.trim();
      const content = p.textContent.replace(label || '', '').trim();

      switch (label) {
        case 'Service Provider:':
          details.serviceProvider = content;
          break;
        case 'Resource:':
          details.resource = content;
          break;
        case 'Date:':
          details.date = content;
          break;
        case 'Time:':
          details.time = content;
          break;
      }
    });

    return details;
  }

  parseBookingOptions(htmlSnippet, confirmationMessage = '') {
    if (!htmlSnippet) return [];

    const confirmation = this.parseConfirmationHtml(confirmationMessage);
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlSnippet, 'text/html');
    const table = doc.querySelector('table');
    if (!table) return [];

    const headers = [...table.querySelectorAll('thead th')].map(th =>
      th.textContent.trim().toLowerCase()
    );
    const columnIndex = label =>
      headers.findIndex(header => header.includes(label));
    const dateIndex = columnIndex('date');
    const timeIndex = columnIndex('time');
    const providerIndex = columnIndex('provider');
    const resourceIndex = columnIndex('resource');
    const alternateIndex = headers.findIndex(
      header => header.includes('alternate') || header.includes('other start')
    );

    if (dateIndex < 0 || timeIndex < 0) return [];

    return [...table.querySelectorAll('tbody tr')]
      .map(row => {
        const cells = [...row.querySelectorAll('td')].map(td =>
          td.textContent.trim()
        );
        const time = cells[timeIndex] || '';
        const [startTime, endTime] = time
          .split(/\s+(?:–|-|to)\s+/i)
          .map(value => value.trim());

        return {
          date: cells[dateIndex],
          startTime,
          endTime,
          serviceProvider: cells[providerIndex] || confirmation.serviceProvider,
          resource: cells[resourceIndex] || confirmation.resource,
          alternateStartTimes: this.parseAlternateStartTimes(
            cells[alternateIndex] || ''
          ),
        };
      })
      .filter(option => option.date && option.startTime && option.endTime);
  }

  renderBookingOptions(htmlSnippet, confirmationMessage) {
    // We only ever surface times the model itself returned (headline times plus
    // the alternate starts it validated). No client-side inference — we make no
    // assumptions about availability the model didn't explicitly give us.
    const options = this.groupOptionsByDay(
      this.parseBookingOptions(htmlSnippet, confirmationMessage)
    );
    if (options.length === 0) return false;

    const container = document.createElement('div');
    container.className = 'booking-options booking-options--student';

    const heading = document.createElement('div');
    heading.className = 'booking-options__heading';
    heading.textContent = 'Choose your preferred appointment time';
    container.appendChild(heading);

    // Surface the appointment length up front — easy to forget when scanning
    // start times.
    const durationLabel = this.slotDurationLabel(options[0]);
    if (durationLabel) {
      const durationNote = document.createElement('div');
      durationNote.className = 'booking-options__duration';
      durationNote.textContent = `${durationLabel} appointment`;
      container.appendChild(durationNote);
    }

    const grid = document.createElement('div');
    grid.className = 'booking-options__grid';

    options.forEach(option => {
      const button = this.buildBookingOptionButton(option);
      button.addEventListener('click', () =>
        this.selectBookingOption(option, button)
      );
      grid.appendChild(button);
    });

    container.appendChild(grid);
    this.availabilityHistoryTarget.appendChild(container);
    this.selectBookingOption(
      options[0],
      grid.querySelector('.booking-options__button')
    );
    return true;
  }

  buildBookingOptionButton(option) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'booking-options__button';

    const time = document.createElement('span');
    time.className = 'booking-options__time';
    time.textContent = `${option.startTime} - ${option.endTime}`;

    const date = document.createElement('span');
    date.className = 'booking-options__date';
    date.textContent = this.formatDateWithDay(option.date);

    const meta = document.createElement('span');
    meta.className = 'booking-options__meta';
    meta.textContent = [option.serviceProvider, option.resource]
      .filter(Boolean)
      .join(' • ');

    button.append(time, date);
    if (meta.textContent) button.appendChild(meta);

    const alternateStarts = this.validAlternateStartTimes(option);
    if (alternateStarts.length > 0) {
      const alternates = document.createElement('span');
      alternates.className = 'booking-options__alternates';

      const label = document.createElement('span');
      label.className = 'booking-options__alternates-label';
      label.textContent = 'Also available';
      alternates.appendChild(label);

      alternateStarts.forEach(startTime => {
        const chip = document.createElement('span');
        chip.className = 'booking-options__alternate-chip';
        chip.textContent = startTime;
        chip.addEventListener('click', event => {
          event.stopPropagation();
          this.selectBookingOption(
            this.optionWithAlternateStart(option, startTime),
            button,
            chip
          );
        });
        alternates.appendChild(chip);
      });

      button.appendChild(alternates);
    }

    return button;
  }

  parseAlternateStartTimes(value) {
    if (!value) return [];

    return value
      .split(/[,;|\n]+/)
      .map(time => time.replace(/^(?:and|or)\s+/i, '').trim())
      .filter(Boolean);
  }

  // Collapse every available slot for a given day (and provider/resource, so the
  // card stays bookable) into a single card: the earliest time becomes the
  // headline and every other available start that day becomes an "Also
  // available" chip.
  groupOptionsByDay(options) {
    const groups = new Map();

    options.forEach(option => {
      const key = [
        option.date,
        option.serviceProvider || '',
        option.resource || '',
      ].join('|');

      if (!groups.has(key)) {
        groups.set(key, { option, starts: [] });
      }

      const group = groups.get(key);
      [option.startTime, ...(option.alternateStartTimes || [])].forEach(time => {
        group.starts.push(time);
      });
    });

    return [...groups.values()].map(({ option, starts }) => {
      const duration =
        this.timeToMinutes(option.endTime) -
        this.timeToMinutes(option.startTime);
      const sortedStarts = this.uniqueTimes(starts).sort(
        (a, b) => this.timeToMinutes(a) - this.timeToMinutes(b)
      );
      const earliest = sortedStarts[0] || option.startTime;
      const earliestMinutes = this.timeToMinutes(earliest);
      const endTime =
        duration > 0 && earliestMinutes !== null
          ? this.minutesToTime(earliestMinutes + duration)
          : option.endTime;

      return {
        date: option.date,
        serviceProvider: option.serviceProvider,
        resource: option.resource,
        startTime: earliest,
        endTime,
        alternateStartTimes: sortedStarts.slice(1),
      };
    });
  }

  uniqueTimes(times) {
    const seen = new Set();

    return times.filter(time => {
      const minutes = this.timeToMinutes(time);
      if (minutes === null || seen.has(minutes)) return false;

      seen.add(minutes);
      return true;
    });
  }

  validAlternateStartTimes(option) {
    return (option.alternateStartTimes || []).filter(startTime => {
      const startMinutes = this.timeToMinutes(startTime);
      const originalStart = this.timeToMinutes(option.startTime);
      return (
        startMinutes !== null &&
        originalStart !== null &&
        startMinutes > originalStart
      );
    });
  }

  optionWithAlternateStart(option, startTime) {
    const duration =
      this.timeToMinutes(option.endTime) - this.timeToMinutes(option.startTime);
    const alternateStart = this.timeToMinutes(startTime);

    if (!duration || duration <= 0 || alternateStart === null) return option;

    return {
      ...option,
      startTime,
      endTime: this.minutesToTime(alternateStart + duration),
    };
  }

  timeToMinutes(value) {
    const match = String(value)
      .trim()
      .match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (!match) return null;

    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2] || '0', 10);
    const meridian = match[3].toUpperCase();

    if (meridian === 'PM' && hours !== 12) hours += 12;
    if (meridian === 'AM' && hours === 12) hours = 0;

    return hours * 60 + minutes;
  }

  minutesToTime(totalMinutes) {
    const normalized = ((totalMinutes % 1440) + 1440) % 1440;
    const hours24 = Math.floor(normalized / 60);
    const minutes = normalized % 60;
    const meridian = hours24 >= 12 ? 'PM' : 'AM';
    const hours12 = hours24 % 12 || 12;
    const minuteText = String(minutes).padStart(2, '0');

    return `${hours12}:${minuteText} ${meridian}`;
  }

  selectBookingOption(option, selectedButton = null, selectedChip = null) {
    const confirmationContainer = this.availabilityHistoryTarget.querySelector(
      '.confirmation-container'
    );

    if (confirmationContainer) {
      confirmationContainer.innerHTML = `
        <p><strong>Service Provider:</strong> ${option.serviceProvider ||
          'none'}</p>
        <p><strong>Resource:</strong> ${option.resource || 'none'}</p>
        <p><strong>Date:</strong> ${option.date}</p>
        <p><strong>Time:</strong> ${option.startTime} to ${option.endTime}</p>
      `;
    }

    this.updateConfirmationButton(
      option.date,
      option.startTime,
      option.endTime
    );

    this.availabilityHistoryTarget
      .querySelectorAll('.booking-options__button')
      .forEach(button =>
        button.classList.remove('booking-options__button--selected')
      );
    this.availabilityHistoryTarget
      .querySelectorAll('.booking-options__alternate-chip')
      .forEach(chip =>
        chip.classList.remove('booking-options__alternate-chip--selected')
      );
    if (selectedButton)
      selectedButton.classList.add('booking-options__button--selected');
    if (selectedChip)
      selectedChip.classList.add('booking-options__alternate-chip--selected');
  }

  updateConfirmationButton(selectedDate, startTime, endTime) {
    if (this.createServiceEventButtonTarget) {
      this.createServiceEventButtonTarget.style.display = 'block';
      // Reflect the currently selected slot so the CTA updates as the student
      // taps different times. Include the weekday for clarity.
      const dateWithDay = this.formatDateWithDay(selectedDate);
      this.createServiceEventButtonTarget.textContent = `Request ${dateWithDay} at ${startTime}`;
      this.createServiceEventButtonTarget.title = `Request booking for ${dateWithDay} ${startTime} - ${endTime}`;
    } else {
      console.error('Create Service Event Button target is not found.');
    }
  }

  // Prepend the weekday to a date string for display (e.g. "June 27, 2026" ->
  // "Friday, June 27, 2026"). Display only — the booking payload keeps the raw
  // date from the model. Leaves the string untouched if it already names a day
  // or can't be parsed.
  formatDateWithDay(dateString) {
    if (!dateString) return dateString;

    const trimmed = String(dateString).trim();
    const firstWord = trimmed.split(/[\s,]+/)[0].toLowerCase();
    const dayPrefixes = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    if (dayPrefixes.some(prefix => firstWord.startsWith(prefix))) {
      return trimmed;
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return trimmed;

    const weekday = parsed.toLocaleDateString('en-US', { weekday: 'long' });
    return `${weekday}, ${trimmed}`;
  }

  // Appointment length label (e.g. "60 min") derived from the slot's own
  // start/end so it always matches what is being booked.
  slotDurationLabel(option) {
    if (!option) return '';
    const minutes =
      this.timeToMinutes(option.endTime) - this.timeToMinutes(option.startTime);
    if (!minutes || minutes <= 0) return '';
    return `${minutes} min`;
  }

  hideConfirmationButton() {
    if (this.createServiceEventButtonTarget) {
      this.createServiceEventButtonTarget.style.display = 'none';
    }
  }

  setAvailabilityVisible(visible) {
    if (this.hasAvailabilitySectionTarget) {
      this.availabilitySectionTarget.classList.toggle('hidden', !visible);
    }
  }

  showLoadingIndicator() {
    if (!document.getElementById('message-text-loading')) {
      const loadingElement = document.createElement('div');
      loadingElement.classList.add('chatgpt-message', 'assistant');
      loadingElement.id = 'message-text-loading';
      loadingElement.innerHTML = `
        <div class="loading-placeholder" style="margin-bottom: 3px;"></div>
      `;
      this.historyTarget.appendChild(loadingElement);
      this.scrollToBottom();
    }
  }

  removeLoadingIndicator() {
    const loadingElement = document.getElementById('message-text-loading');
    if (loadingElement) {
      this.historyTarget.removeChild(loadingElement);
    }
  }

  scrollToBottom() {
    this.historyTarget.scrollTop = this.historyTarget.scrollHeight;
  }

  askNextClassAvailability() {
    this.inputTarget.value = 'What is the next available appointment time?';
    this.send(new Event('click'));
  }

  // Picking a date must NOT send anything on its own. On mobile, the native
  // date wheel fires `change` on every adjustment, which previously fired a
  // booking message per tick. Instead we just record the chosen date and flip
  // the single CTA to "Check date"; the student taps it to send the request.
  dateSelected(event) {
    this.selectedDate = event.target.value; // 'YYYY-MM-DD'
    this.updateDatePlaceholder();
    this.updatePrimaryActionLabel();
  }

  // One call-to-action button: with no date chosen it checks the next available
  // slot; once a date is picked it checks that specific date.
  runPrimaryAction() {
    // Running the first check switches the panel from the tools to the chat —
    // the controls aren't needed once a conversation is underway.
    this.enterChatMode();

    const selectedDate =
      this.selectedDate ||
      (this.hasChooseDateFieldTarget ? this.chooseDateFieldTarget.value : '');
    if (selectedDate) {
      this.sendDateQuery(selectedDate);
    } else {
      this.askNextClassAvailability();
    }
  }

  // Tools-only phase: availability controls visible, chat hidden.
  resetToToolsMode() {
    if (this.hasToolsTarget) this.toolsTarget.classList.remove('hidden');
    if (this.hasChatConversationTarget) {
      this.chatConversationTarget.classList.add('hidden');
    }
  }

  // Chat phase: hide the controls, reveal the conversation.
  enterChatMode() {
    if (this.hasToolsTarget) this.toolsTarget.classList.add('hidden');
    if (this.hasChatConversationTarget) {
      this.chatConversationTarget.classList.remove('hidden');
    }
  }

  updatePrimaryActionLabel() {
    if (!this.hasPrimaryActionButtonTarget) return;
    const hasDate = Boolean(
      this.selectedDate ||
        (this.hasChooseDateFieldTarget && this.chooseDateFieldTarget.value)
    );
    this.primaryActionButtonTarget.textContent = hasDate
      ? 'Check date'
      : 'Check next availability';
  }

  resetDateSelection() {
    this.selectedDate = '';
    if (this.hasChooseDateFieldTarget) {
      this.chooseDateFieldTarget.value = '';
    }
    this.updateDatePlaceholder();
    this.updatePrimaryActionLabel();
  }

  // iOS Safari shows no placeholder for an empty <input type="date">, leaving a
  // blank box. Toggle a class so CSS can render a faux placeholder when empty.
  updateDatePlaceholder() {
    if (!this.hasChooseDateFieldTarget) return;
    this.chooseDateFieldTarget.classList.toggle(
      'is-empty',
      !this.chooseDateFieldTarget.value
    );
  }

  sendDateQuery(selectedDate) {
    //We do the following to avoid having issues with timezones, to make sure the date that is picked by the user is the one we send on the string below
    const [year, month, day] = selectedDate.split('-').map(Number);
    const date = new Date(year, month - 1, day, 12, 0, 0);

    // Format the date to a more natural language format
    const formattedDate = date.toLocaleDateString('en-US', {
      weekday: 'long', // Full name of the day (e.g., 'Tuesday')
      month: 'short', // Short name of the month (e.g., 'Sep')
      day: '2-digit', // Two-digit day (e.g., '03')
      year: 'numeric', // Full numeric year (e.g., '2024')
    });

    this.inputTarget.value = `I would like to book on ${formattedDate}?`;
    this.send(new Event('click'));
  }

  createRequest() {
    console.log('createRequest called.');

    // Parse the confirmation message displayed on the page
    const confirmationContainer = document.querySelector(
      '.confirmation-container'
    );
    if (!confirmationContainer) {
      console.error('No confirmation container found.');
      alert('No confirmation message found.');
      return;
    }

    const paragraphs = confirmationContainer.querySelectorAll('p');
    if (paragraphs.length === 0) {
      console.error('No confirmation message found.');
      alert('No confirmation message found.');
      return;
    }

    let serviceProviderNames = [];
    let resourceNames = [];
    let dateText = null;
    let timeText = null;

    // Extract relevant data from the confirmation message
    paragraphs.forEach(p => {
      const strongText = p.querySelector('strong')?.textContent.trim();
      const content = p.textContent.replace(strongText, '').trim();

      if (content.toLowerCase() === 'none') {
        return; // Skip 'none' entries
      }

      switch (strongText) {
        case 'Service Provider:':
          serviceProviderNames = content.split(',').map(name => name.trim());
          break;
        case 'Resource:':
          resourceNames = content.split(',').map(name => name.trim());
          break;
        case 'Date:':
          dateText = content;
          break;
        case 'Time:':
          timeText = content;
          break;
        default:
          console.warn(`Unexpected field: ${strongText}`);
      }
    });

    console.log('Extracted Service Providers:', serviceProviderNames);
    console.log('Extracted Resources:', resourceNames);
    console.log('Extracted Date:', dateText);
    console.log('Extracted Time:', timeText);

    if (!dateText || !timeText) {
      console.error('Date or Time could not be extracted.');
      alert('Invalid or missing date/time in the confirmation message.');
      return;
    }

    const [startTimeString, endTimeString] = timeText
      .split('to')
      .map(time => time.trim());

    // Match with dropdown options
    const serviceProviderSelect = document.getElementById(
      'service_provider_select'
    );
    const bookableResourceSelect = document.getElementById(
      'bookable_resource_select'
    );

    const serviceProviderIds = Array.from(serviceProviderSelect.options)
      .filter(option =>
        serviceProviderNames.some(
          name => name.toLowerCase() === option.textContent.trim().toLowerCase()
        )
      )
      .map(option => option.value);

    const resourceIds = Array.from(bookableResourceSelect.options)
      .filter(option =>
        resourceNames.some(
          name => name.toLowerCase() === option.textContent.trim().toLowerCase()
        )
      )
      .map(option => option.value);

    console.log('Matched Service Provider IDs:', serviceProviderIds);
    console.log('Matched Resource IDs:', resourceIds);

    // Prepare and send payload
    const payload = {
      service_event_request: {
        user_id: document.getElementById('user_id').value,
        subdomain_id: document.getElementById('subdomain_id').value,
        service_id: document.querySelector('[name=service_id]').value,
        start_time: `${dateText} ${startTimeString}`,
        end_time: `${dateText} ${endTimeString}`,
        status: 'requested',
      },
      resource_service_event_requests: [
        ...serviceProviderIds.map(id => ({
          bookable_resource_id: id,
          start_time: `${dateText} ${startTimeString}`,
          end_time: `${dateText} ${endTimeString}`,
        })),
        ...resourceIds.map(id => ({
          bookable_resource_id: id,
          start_time: `${dateText} ${startTimeString}`,
          end_time: `${dateText} ${endTimeString}`,
        })),
      ],
    };

    console.log('Payload:', JSON.stringify(payload, null, 2));

    fetch('/student/services/confirm_time', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
      },
      body: JSON.stringify(payload),
    })
      .then(response => response.json())
      .then(data => {
        if (data.service_event_request_id) {
          console.log('Service Event Request created successfully:', data);
          this.showSuccessMessage(); // Show success message
        } else {
          console.error('Service event request creation failed:', data.errors);
        }
      })
      .catch(error =>
        console.error('Error creating service event request:', error)
      );
  }

  // Helper function to convert 12-hour to 24-hour format
  convertTo24Hour(hour, isPM) {
    if (isPM && hour !== '12') {
      return parseInt(hour, 10) + 12;
    } else if (!isPM && hour === '12') {
      return 0;
    }
    return parseInt(hour, 10);
  }

  // Helper function to match IDs based on text content of options
  getMatchingIds(selectId, names) {
    const selectElement = document.getElementById(selectId);
    if (!selectElement) {
      console.error(`Element with ID '${selectId}' not found.`);
      return [];
    }

    const selectedIds = [];
    Array.from(selectElement.options).forEach(option => {
      if (
        names.some(
          name =>
            name.toLowerCase().trim() ===
            option.textContent.toLowerCase().trim()
        )
      ) {
        selectedIds.push(option.value);
      }
    });

    return selectedIds;
  }

  showSuccessMessage() {
    // Fetch the success message partial
    fetch('/student/services/success_message')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to load success message partial.');
        }
        return response.text();
      })
      .then(html => {
        // Insert the success message HTML into the body
        const overlay = document.createElement('div');
        overlay.classList.add('success-overlay');
        overlay.innerHTML = html;
        document.body.appendChild(overlay);

        // Hide the form and other content
        const studentServicesWidget = document.querySelector(
          '.student-services-widget'
        );
        if (studentServicesWidget) {
          studentServicesWidget.style.display = 'none';
        }

        console.log('Success message overlay displayed.');
      })
      .catch(error => console.error('Error fetching success message:', error));
  }

  toggleDatePicker() {
    const chooseDateField = this.chooseDateFieldTarget;
    if (chooseDateField.classList.contains('hidden')) {
      chooseDateField.classList.remove('hidden');
    } else {
      chooseDateField.classList.add('hidden');
    }
  }
}
